import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { supabase } from '../../lib/supabase';
import { embedOne, formatTime } from '../../lib/utils';
import { Avatar, Button, ChatComposer, IconButton, MessageBubble, SearchInput, Spinner, SystemEvent, TripContextBar } from '../../components/design-system';
import { ReactionPicker } from '../../components/chat/ReactionPicker';
import { useMessageReactions } from '../../hooks/useMessageReactions';
import { useConversationMessages } from '../../hooks/useConversationMessages';
import { deriveHostStage, formatRange, STAGE_DOT_COLOR, STAGE_LABEL, type HostTripStage } from '../../lib/hostConversations';
import type { Message } from '../../types';
import { scrollListToBottom } from '../../lib/keyboard';

interface ChatMeta {
  conversationId: string;
  travelerId: string;
  travelerName: string | null;
  travelerAvatar: string | null;
  messages: Message[];
  stage: HostTripStage;
  dates: string | null;
  dayOf: string | null;
}

function ChatMessage({ msg, travelerName, travelerAvatar, userId, onReact }: {
  msg: Message;
  travelerName: string | null;
  travelerAvatar: string | null;
  userId: string;
  onReact: (emoji: string) => void;
}) {
  if (msg.role === 'system') return <SystemEvent className="py-2">{msg.content}</SystemEvent>;

  const isMe = msg.sender === userId;
  // Alma is identified by role, not by sender: messages drafted in the
  // anonymous /try chat carry no `sender`, so the traveler's own history would
  // otherwise be attributed to Alma here.
  const isAlma = !isMe && (msg.role === 'assistant' || msg.sender === 'alma');
  const isTraveler = !isMe && !isAlma;

  const senderLabel = isAlma ? 'Alma' : isTraveler ? (travelerName?.split(' ')[0] ?? 'Traveler') : undefined;
  const avatar = isMe
    ? undefined
    : isAlma
      ? <Avatar kind="alma" size={32} />
      : <Avatar kind="member" size={32} src={travelerAvatar ?? undefined} name={travelerName ?? 'Traveler'} />;

  return (
    <ReactionPicker align={isMe ? 'right' : 'left'} onReact={onReact} currentReaction={msg.reaction}>
      <MessageBubble
        from={isMe ? 'you' : 'other'}
        senderName={senderLabel}
        time={formatTime(msg.timestamp)}
        avatar={avatar}
        reaction={msg.reaction}
      >
        {msg.content}
      </MessageBubble>
    </ReactionPicker>
  );
}

export function HostChat() {
  const { matchId } = useParams<{ matchId: string }>();
  const { user, profile } = useAuth();
  const [meta, setMeta] = useState<ChatMeta | null>(null);
  const [loading, setLoading] = useState(true);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [messageQuery, setMessageQuery] = useState('');
  const navigate = useNavigate();
  const bottomRef = useRef<HTMLDivElement>(null);

  function setMetaMessages(updater: (prev: Message[]) => Message[]) {
    setMeta((prev) => (prev ? { ...prev, messages: updater(prev.messages) } : prev));
  }
  const { react } = useMessageReactions(meta?.conversationId, setMetaMessages);

  useEffect(() => {
    if (!user || !matchId) return;
    void (async () => {
      const { data: match } = await supabase
        .from('matches')
        .select(`
          traveler_id,
          status,
          traveler_profile:profiles!matches_traveler_id_fkey(full_name, avatar_url),
          conversation:conversations(id, messages),
          itinerary:itineraries(status, content)
        `)
        .eq('id', matchId)
        .eq('host_id', user.id)
        .maybeSingle();

      if (!match) { setLoading(false); return; }
      const raw = match as Record<string, unknown>;
      const tp = raw.traveler_profile as { full_name: string | null; avatar_url: string | null } | null;
      const convRows = raw.conversation as Array<{ id: string; messages: Message[] }> | null;
      const conv = convRows?.[0] ?? null;

      const itin = embedOne(raw.itinerary as { status: string; content: Record<string, unknown> | null } | { status: string; content: Record<string, unknown> | null }[] | null);
      const content = itin?.content ?? null;
      const start = content?.start_date ? new Date(String(content.start_date)) : null;
      const end = content?.end_date ? new Date(String(content.end_date)) : null;
      const now = new Date();
      const stage = deriveHostStage(String(raw.status ?? ''), itin?.status ?? null, start, end, now);

      let dayOf: string | null = null;
      if (stage === 'active' && start && end) {
        const total = Math.round((end.getTime() - start.getTime()) / 86400000) + 1;
        const day = Math.min(Math.floor((now.getTime() - start.getTime()) / 86400000) + 1, total);
        dayOf = `Day ${day} of ${total}`;
      }

      setMeta({
        conversationId: conv?.id ?? '',
        travelerId: (raw.traveler_id as string) ?? '',
        travelerName: tp?.full_name ?? null,
        travelerAvatar: tp?.avatar_url ?? null,
        messages: conv?.messages ?? [],
        stage,
        dates: formatRange(start, end),
        dayOf,
      });
      setLoading(false);
    })();
  }, [user, matchId]);

  useEffect(() => {
    scrollListToBottom(bottomRef.current);
  }, [meta?.messages]);

  useConversationMessages(meta?.conversationId, (messages) =>
    setMeta((prev) => (prev ? { ...prev, messages } : prev)),
  );

  async function handleSend() {
    if (!input.trim() || !meta?.conversationId || !user) return;
    setSending(true);
    const content = input.trim();
    const newMsg: Message = {
      role: 'user',
      sender: user.id,
      content,
      timestamp: new Date().toISOString(),
    };
    setMeta((prev) => prev ? { ...prev, messages: [...prev.messages, newMsg] } : prev);
    setInput('');
    const { error: rpcError } = await supabase.rpc('append_chat_message', {
      p_conv_id: meta.conversationId,
      p_msg: newMsg,
    });
    if (rpcError) console.error('append_chat_message failed:', rpcError);

    if (meta.travelerId) {
      const { data: { session } } = await supabase.auth.getSession();
      if (session) {
        const supabaseUrl = import.meta.env.VITE_SUPABASE_URL as string;
        fetch(`${supabaseUrl}/functions/v1/send-push`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${session.access_token}`,
            apikey: import.meta.env.VITE_SUPABASE_ANON_KEY as string,
          },
          body: JSON.stringify({
            user_id: meta.travelerId,
            title: profile?.full_name?.split(' ')[0] ?? 'Host',
            body: content.slice(0, 100),
            data: { type: 'chat', match_id: matchId },
          }),
        }).catch(() => {});
      }
    }
    setSending(false);
  }

  if (loading) return <div className="flex justify-center py-16"><Spinner /></div>;

  if (!meta?.conversationId) {
    return (
      <div className="flex flex-col items-center justify-center h-full px-6 text-center gap-3">
        <p className="text-sm text-app-muted">Chat isn't available yet — the traveler hasn't confirmed the match.</p>
      </div>
    );
  }

  const travelerFirst = meta?.travelerName?.split(' ')[0] ?? 'Traveler';
  const stage = meta?.stage ?? 'building';
  const q = messageQuery.trim().toLowerCase();
  const visibleMessages = q
    ? meta!.messages.filter((m) => m.content.toLowerCase().includes(q))
    : meta!.messages;

  return (
    <div className="flex flex-col h-full">
      {/* Thread header */}
      <header className="shrink-0 border-b border-app-border">
        <div className="flex items-center gap-3.5 px-4 md:px-6 py-4">
          <Avatar kind="member" size={44} src={meta?.travelerAvatar ?? undefined} name={meta?.travelerName ?? 'Traveler'} />
          <div className="flex-1 min-w-0">
            <div className="font-serif text-app-fg truncate" style={{ fontSize: 19, lineHeight: 1.1 }}>{meta?.travelerName ?? 'Traveler'}</div>
            <div className="text-app-muted text-[11px] mt-[3px] flex items-center gap-1.5">
              <span className="w-[5px] h-[5px] rounded-full shrink-0" style={{ background: STAGE_DOT_COLOR[stage] }} />
              <span>{STAGE_LABEL[stage]}</span>
            </div>
          </div>
          <div className="flex items-center gap-1">
            <IconButton
              icon="search"
              iconSize={16}
              label="Search in conversation"
              onClick={() => { setSearchOpen((o) => !o); setMessageQuery(''); }}
            />
          </div>
        </div>
        {searchOpen && (
          <div className="px-4 md:px-6 pb-3">
            <SearchInput value={messageQuery} onChange={setMessageQuery} placeholder="Search in conversation" autoFocus />
          </div>
        )}
      </header>

      {/* Trip context bar */}
      <TripContextBar
        statusLabel={STAGE_LABEL[stage]}
        statusColor={STAGE_DOT_COLOR[stage]}
        dates={meta?.dates ?? undefined}
        dayOf={meta?.dayOf ?? undefined}
        action={
          <Button variant="outline" leadingIcon="clipboard-list" onClick={() => navigate(`/host/itinerary/${matchId}`)} style={{ fontSize: 12, padding: '7px 15px' }}>
            Open handbook
          </Button>
        }
      />

      <div className="flex-1 overflow-y-auto px-4 md:px-7 py-4 flex flex-col gap-3.5" data-keyboard-pin>
        {(!meta?.messages || meta.messages.length === 0) && (
          <div className="flex justify-center py-8">
            <p className="text-xs text-app-muted">Your conversation will appear here.</p>
          </div>
        )}
        {q && visibleMessages.length === 0 && (
          <div className="flex justify-center py-8">
            <p className="text-xs text-app-muted">No messages match "{messageQuery}".</p>
          </div>
        )}
        {visibleMessages.map((msg) => {
          const i = meta!.messages.indexOf(msg);
          return (
            <ChatMessage
              key={i}
              msg={msg}
              travelerName={meta!.travelerName}
              travelerAvatar={meta!.travelerAvatar}
              userId={user?.id ?? ''}
              onReact={(emoji) => void react(i, emoji)}
            />
          );
        })}
        <div ref={bottomRef} />
      </div>

      {/* Composer + host safety line */}
      <div className="shrink-0 px-4 md:px-7 pt-3 pb-5 pb-safe-or-5">
        <ChatComposer
          value={input}
          onChange={setInput}
          onSend={() => void handleSend()}
          placeholder={`Message ${travelerFirst}…`}
          disabled={sending}
          disclaimer="Keep guest details in chat — never request passport or payment info here."
        />
      </div>
    </div>
  );
}

export default HostChat;
